import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {FontHeading} from '@Components/font-selector-panel/components/font-heading';
import {DottedIconButton} from '@Components/dotted-icon-button';
import {getValidFontFiles, startUpload} from '@Libraries/font-upload-library';
import {FontsList} from '@Components/font-selector-panel/components/fonts-list';
import type {OnDeleteFontItemCallback, OnFontFamilyUploaded, OnSelectFontFamilyCallback} from '@Components/font-selector-panel/font-selector.types';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {openUploadFontAgreementModal} from '@Modals/upload-font-agreement-modal';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {openErrorModal} from '@Modals/error-modal';
import {removeUserFont} from '@Components/font-selector/font-selector-slice';
import type {FontData} from '@Libraries/font-library';
import {getUserFontsFromStore} from '@Libraries/font-library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './font-upload-section.module.scss';
import {noop} from "@Utils/general.util";

interface FontUploadSectionProps {
  className?: string;
  onDeleteFontItem: OnDeleteFontItemCallback;
  onSelectFontFamily?: OnSelectFontFamilyCallback;
  onFileUploadedCallback?: OnFontFamilyUploaded;
  selectedFontFamily?: string;
  isSelectedFontLoading?: boolean;
}

export function FontUploadSection({
  className = '',
  selectedFontFamily = '',
  isSelectedFontLoading = false,
  onSelectFontFamily = (): void => {},
  onFileUploadedCallback = (): void => {},
  ...props
}: FontUploadSectionProps): ReactElement<any> {
  const uploadFontInputRef = useRef<HTMLInputElement>(null);
  const userFontData = useAppSelector((state) => {
    return state.fontSelector.userFonts as Array<FontData> | null;
  });
  const dispatch = useAppDispatch();
  const [isUploading, setIsUploadingFont] = useState<boolean>(false);

  const isUploadAllowed = () => {
    return isUserLoggedIn() && isUserPremium();
  };

  const showErrorModal = (): void => {
    openErrorModal({
      message: window.i18next.t('pmwjs_error_message'),
    });
  };

  const areFilesValid = (e: any) => {
    const validFiles = getValidFontFiles(e.target.files);
    if (validFiles.length !== e.target.files.length) {
      showErrorModal();
      return null;
    }
    return validFiles;
  };

  const openAgreementModal = (validFiles: any) => {
    openUploadFontAgreementModal({
      onAgree: () => {
        return onAgreementSuccess(validFiles);
      },
    });
  };

  const handleUpload = (): void => {
    if (!isUploadAllowed()) {
        window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_CUSTOM_FONT);
      return;
    }

    if (uploadFontInputRef && uploadFontInputRef.current) {
      uploadFontInputRef.current.click();
    }
  };

  const getUserFontDataFromFontFamily = (fontUploaded: string): FontData | undefined => {
    const userFontsDataArray = getUserFontsFromStore() as Array<FontData> | null;
    if (userFontsDataArray) {
      for (const eachUserFont of userFontsDataArray) {
        if (eachUserFont.fontFamily === fontUploaded) {
          return eachUserFont;
        }
      }
    }
    return undefined;
  };

  const onFileUploaded = (fontFamily: string): void => {
    if (!isUploadAllowed()) {
        window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_CUSTOM_FONT);
      return;
    }
    onFileUploadedCallback(fontFamily, getUserFontDataFromFontFamily(fontFamily));
    setIsUploadingFont(false);
    trackPosterBuilderGA4Events(GA4EventName.UPLOAD_FONT);
  };

  const onAgreementSuccess = async (validFiles: any) => {
    setIsUploadingFont(true);
    await startUpload(validFiles, onFileUploaded);
  };

  const onFileUpload = (e: any) => {
    if (uploadFontInputRef && uploadFontInputRef.current) {
      const validFiles = areFilesValid(e);
      if (validFiles) {
        openAgreementModal(validFiles);
      }
    }
    return null;
  };

  const deleteFontItem = async (fontFamily: string): Promise<void> => {
    return window.PMW.readLocal('font/removeUserFont', {ff: fontFamily}).then((): void => {
      // @ts-ignore
      dispatch(removeUserFont(fontFamily));
    });
  };

  const getUserFonts = () => {
    if (!userFontData) {
      return null;
    }

    return (
      <FontsList
        selectedFontFamily={selectedFontFamily}
        isSelectedFontLoading={isSelectedFontLoading}
        onSelectFontFamily={onSelectFontFamily}
        onDeleteFontItem={(font: FontData): void => {
          void deleteFontItem(font.fontFamily);
          props.onDeleteFontItem(font);
        }}
        isUserUploadedFontList
        className="spacing-m-t-2"
        fontNames={userFontData}
      />
    );
  };

  return (
    <div className={`${className} ${styles.uploadContainer} flex-v-row spacing-p-t-4 spacing-p-b-3`}>
      <FontHeading heading={window.i18next.t('pmwjs_uploaded')} showPremiumIcon={!isUploadAllowed()} />
      <div className="_hidden flexbox">
        <input ref={uploadFontInputRef} type="file" id="file-font-upload" className="hidden flex-1" name="Filedata" multiple accept=".ttf, .otf" onChange={onFileUpload} />
      </div>
      <DottedIconButton
        isLoading={isUploading}
        disabled={isUploading}
        onClick={handleUpload}
        textClasses="body-xs content-sub-text capitalize-sentence"
        direction="horizontal"
        className={`${styles.uploadedButton} spacing-m-t-2`}
        btnIcon="icon-upload-1 font-icon-upload"
        btnText={window.i18next.t('pmwjs_upload_font')}
      />
      {getUserFonts()}
    </div>
  );
}
